// Персонализация: палитра, кегль и плотность отступов. Всё применяется сразу —
// экран сам перекрашивается, меняет кегль и поджимается, поэтому отдельного
// превью не нужно: он и есть превью.
//
// Панель отделена от модала: она встраивается в попап пользователя, где живёт
// вместе с настройками аккаунта, а не отдельным окном.
import type { ChangeEvent, PointerEvent, ReactElement } from 'react';
import { useT } from '../i18n';
import { useTheme } from './ThemeProvider';
import {
  FONT_SCALES,
  FONT_SCALE_LABEL_KEYS,
  SPACE_SCALES,
  SPACE_SCALE_LABEL_KEYS,
  THEMES,
  THEME_IDS,
  type ThemeId,
} from './themes';
import styles from './PersonalizationModal.module.css';

/**
 * Полоса выбора темы — это не обычный range: клик и перетаскивание должны
 * попадать ровно в ту полоску, на которую смотрит палец, а нативная раскладка
 * отсчитывает значения от центра бегунка и у краёв промахивается на полполоски.
 * Поэтому позицию считаем сами, а сам input остаётся ради клавиатуры и роли.
 */
function bandAt(el: HTMLElement, clientX: number, count: number): number {
  const rect = el.getBoundingClientRect();
  if (rect.width === 0) return 0;
  const band = Math.floor(((clientX - rect.left) / rect.width) * count);
  return Math.min(count - 1, Math.max(0, band));
}

/**
 * Ступенчатый ползунок: слева и справа — знаки «меньше» и «больше», под ним
 * название ступени и процент. Кегль и отступы устроены одинаково, поэтому
 * живут одним компонентом — разъехаться в вёрстке им незачем.
 */
function StepSlider({
  legend,
  index,
  last,
  name,
  percent,
  onChange,
  marks,
}: {
  legend: string;
  index: number;
  last: number;
  name: string;
  percent: number;
  onChange(index: number): void;
  marks: [ReactElement, ReactElement];
}): ReactElement {
  return (
    <section className={styles.section}>
      <h3 className={styles.legend}>{legend}</h3>

      <div className={styles.sizeRow}>
        <span className={styles.mark} aria-hidden="true">
          {marks[0]}
        </span>
        <input
          type="range"
          className={styles.sizeRange}
          min={0}
          max={last}
          step={1}
          value={index}
          onChange={(e) => onChange(Number(e.target.value))}
          aria-label={legend}
          aria-valuetext={name}
        />
        <span className={styles.mark} aria-hidden="true">
          {marks[1]}
        </span>
      </div>

      <p className={styles.readout} aria-live="polite">
        <span className={styles.readoutName}>{name}</span>
        <span className={`${styles.readoutMeta} mono`}>{percent}%</span>
      </p>
    </section>
  );
}

export function PersonalizationPanel(): ReactElement {
  const t = useT();
  const { theme, meta, fontScaleIndex, spaceScaleIndex, setTheme, setFontScaleIndex, setSpaceScaleIndex } =
    useTheme();

  const themeIndex = THEME_IDS.indexOf(theme);
  const lastTheme = THEME_IDS.length - 1;

  function pickTheme(index: number): void {
    const next: ThemeId | undefined = THEME_IDS[index];
    if (next && next !== theme) setTheme(next);
  }

  function handleThemeDown(e: PointerEvent<HTMLInputElement>): void {
    // preventDefault снимает нативное перетаскивание — позицию ведём сами.
    e.preventDefault();
    e.currentTarget.focus();
    // Сначала выбор, потом захват: захват нужен только для протяжки, и его
    // отказ (бывает на синтетических событиях) не должен съедать сам клик.
    pickTheme(bandAt(e.currentTarget, e.clientX, THEME_IDS.length));
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // без захвата протяжка просто не подхватится — клик уже сработал
    }
  }

  function handleThemeMove(e: PointerEvent<HTMLInputElement>): void {
    // Тянем только при зажатой кнопке: захват указателя переживает потерянный
    // pointerup, и без этой проверки полоса продолжила бы ходить за курсором.
    if (e.buttons === 0 || !e.currentTarget.hasPointerCapture(e.pointerId)) return;
    pickTheme(bandAt(e.currentTarget, e.clientX, THEME_IDS.length));
  }

  function handleThemeKey(e: ChangeEvent<HTMLInputElement>): void {
    pickTheme(Number(e.target.value));
  }

  const modeLabel = t(meta.mode === 'dark' ? 'person.mode.dark' : 'person.mode.light');

  return (
    <>
    <section className={styles.section}>
      <h3 className={styles.legend}>{t('person.theme')}</h3>

      <div className={styles.slider}>
        <div className={styles.bands} aria-hidden="true">
          {THEME_IDS.map((id) => (
            <span
              key={id}
              className={styles.band}
              style={{ background: THEMES[id].swatch.bg }}
            >
              <i className={styles.bandAccent} style={{ background: THEMES[id].swatch.accent }} />
            </span>
          ))}
        </div>

        <input
          type="range"
          className={styles.themeRange}
          min={0}
          max={lastTheme}
          step={1}
          value={themeIndex}
          onChange={handleThemeKey}
          onPointerDown={handleThemeDown}
          onPointerMove={handleThemeMove}
          aria-label={t('person.theme')}
          aria-valuetext={`${meta.name} — ${modeLabel}`}
        />

        {/* Рамка стоит по центру своей полоски, поэтому позиция считается
            от количества полос, а не от диапазона значений ползунка. */}
        <span
          className={styles.marker}
          style={{ left: `${((themeIndex + 0.5) / THEME_IDS.length) * 100}%` }}
          aria-hidden="true"
        />
      </div>

      {/* Название темы — под ползунком: оно меняется вместе с положением. */}
      <p className={styles.readout} aria-live="polite">
        <span className={styles.readoutName}>{meta.name}</span>
        <span className={styles.readoutMeta}>{modeLabel}</span>
      </p>
    </section>

    <StepSlider
      legend={t('person.size')}
      index={fontScaleIndex}
      last={FONT_SCALES.length - 1}
      name={t(FONT_SCALE_LABEL_KEYS[fontScaleIndex])}
      percent={Math.round(FONT_SCALES[fontScaleIndex] * 100)}
      onChange={setFontScaleIndex}
      marks={[
        <span key="sm" className={styles.markSm}>
          A
        </span>,
        <span key="lg" className={styles.markLg}>
          A
        </span>,
      ]}
    />

    <StepSlider
      legend={t('person.space')}
      index={spaceScaleIndex}
      last={SPACE_SCALES.length - 1}
      name={t(SPACE_SCALE_LABEL_KEYS[spaceScaleIndex])}
      percent={Math.round(SPACE_SCALES[spaceScaleIndex] * 100)}
      onChange={setSpaceScaleIndex}
      marks={[
        <span key="sm" className={styles.markSpaceSm} />,
        <span key="lg" className={styles.markSpaceLg} />,
      ]}
    />

    <p className={styles.note}>{t('person.note')}</p>
    </>
  );
}
